import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Header from '../../components/MarginalsV2/Header';
import Footer from '../../components/MarginalsV2/Footer';
import './style.css';
import useStateWithLocalStorage from '../../helpers/useStateWithLocalStorage';
import { parseJwt } from '../../helpers/auth';
import useAdminImageUpload from '../../hooks/useAdminImageUpload';
import { useAuth } from '../../contexts/AuthContext';

export default function Document() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [document, setDocument] = useState(null);
  const [user, setUser] = useState(null);
  const [smsStatus, setSmsStatus] = useState("send");
  const [userStatus, setUserStatus] = useState("save");
  const [docStatus, setDocStatus] = useState("save");
  const [approveStatus, setApproveStatus] = useState("approve");
  const [lowQualityStatus, setLowQualityStatus] = useState("lowQualityAction");
  const [invalidImagesStatus, setInvalidImagesStatus] = useState("invalidImagesAction");
  const [refundStatus, setRefundStatus] = useState("refundAction");
  const [message, setMessage] = useState("Please reply with new images of the front and the back of your driving license.");
  const [documentEdit, setDocumentEdit] = useState("");
  const [userEdit, setUserEdit] = useState("");
  let { docId } = useParams();
  let profileImage = document && document.nationalLicenseAttributes && document.nationalLicenseAttributes.frontImageUrl ? document.nationalLicenseAttributes.frontImageUrl.replace("upload", "upload/w_2.0/w_300,h_350,c_thumb,g_face,e_sharpen,e_improve") : ""
  if (profileImage && document?.nationalLicenseAttributes?.frontImageRotationDegree !== 0) {
    profileImage = profileImage.replace("w_300,h_350",`a_${document.nationalLicenseAttributes.frontImageRotationDegree},w_300,h_350`)
  }

  // Check if user is admin
  const isAdmin = token ? parseJwt(token).isAdmin : false;

  // Memoized callback for image updates
  const handleImageUpdate = useCallback((updatedDoc) => {
    setDocument(updatedDoc);
    setDocumentEdit(JSON.stringify(updatedDoc, undefined, 2));
  }, []);

  // Admin image upload hook
  const { isUploadingFront, isUploadingBack, frontImageWidget, backImageWidget, startClipboardPaste, cancelClipboardPaste, clipboardTarget } = useAdminImageUpload(
    docId,
    token,
    handleImageUpdate,
    user?.email
  );

  useEffect(() => {
    setDocumentEdit(JSON.stringify(document, undefined, 2))
  }, [document])

  useEffect(() => {
    setUserEdit(JSON.stringify(user, undefined, 2))
  }, [user])

  // Cleanup widgets on unmount or when widgets change
  useEffect(() => {
    return function cleanupListener() {
      if (frontImageWidget) frontImageWidget.destroy();
      if (backImageWidget) backImageWidget.destroy();
    }
  }, [frontImageWidget, backImageWidget]);

  useEffect(() => {
    async function fetchData() {
      // Reset all action statuses when loading a new document
      setSmsStatus('send');
      setUserStatus('save');
      setDocStatus('save');
      setApproveStatus('approve');
      setLowQualityStatus('lowQualityAction');
      setInvalidImagesStatus('invalidImagesAction');
      setRefundStatus('refundAction');

      const docResponse = await fetch(
        `${process.env.REACT_APP_API_SERVER_URL}/api/documents/${docId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const docResult = await docResponse.json();
      console.log(docResult)
      setDocument(docResult);

      const userResponse = await fetch(
        `${process.env.REACT_APP_API_SERVER_URL}/api/user/${docResult.userMail}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const userResult = await userResponse.json();
      console.log(userResult)
      setUser(userResult);
    }

    fetchData();

  }, [docId, token]);

  useEffect(() => {
    const updateUser = async () => {
      const settings = {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        body: userEdit,
      };

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_SERVER_URL}/api/user/`,
          settings
        );
  
        console.log(response)
        if (response.status === 200) {
          setUserStatus('saved')
          setUser(JSON.parse(userEdit))
        } else {
          setUserStatus('failed')
        }  
      } catch (e) {
        console.log(e)
        setUserStatus('failed')
      }
    }

    if (userStatus === "saving") {
      //send the sms and set status to 'sent' or 'failed'
      updateUser();
    }
  }, [userStatus]);


  useEffect(() => {
    const updateDocument = async () => {
      const settings = {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        body: documentEdit,
      };

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_SERVER_URL}/api/documents/`,
          settings
        );
  
        console.log(response)
        if (response.status === 200) {
          setDocStatus('saved')
          setDocument(JSON.parse(documentEdit))
        } else {
          setDocStatus('failed')
        }  
      } catch (e) {
        console.log(e)
        setDocStatus('failed')
      }
    }

    if (docStatus === "saving") {
      //send the sms and set status to 'sent' or 'failed'
      updateDocument();
    }
  }, [docStatus]);

  // Handle approve action
  useEffect(() => {
    const approveDocument = async () => {
      const settings = {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          _id: document._id,
          status: "ready"
        }),
      };

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_SERVER_URL}/api/documents/`,
          settings
        );
  
        console.log(response)
        if (response.status === 200) {
          setApproveStatus('approved')
          const updatedDoc = await response.json();
          setDocument(updatedDoc);
          setDocumentEdit(JSON.stringify(updatedDoc, undefined, 2));
          setTimeout(() => setApproveStatus('approve'), 3000); // Reset after 3 seconds
        } else {
          setApproveStatus('failed')
          setTimeout(() => setApproveStatus('approve'), 3000); // Reset after 3 seconds
        }  
      } catch (e) {
        console.log(e)
        setApproveStatus('failed')
        setTimeout(() => setApproveStatus('approve'), 3000); // Reset after 3 seconds
      }
    }

    if (approveStatus === "approving") {
      approveDocument();
    }
  }, [approveStatus, document]);

  // Handle low quality action
  useEffect(() => {
    const markLowQuality = async () => {
      const settings = {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          _id: document._id,
          status: "lowQuality"
        }),
      };

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_SERVER_URL}/api/documents/`,
          settings
        );
  
        console.log(response)
        if (response.status === 200) {
          setLowQualityStatus('lowQualityProcessed')
          const updatedDoc = await response.json();
          setDocument(updatedDoc);
          setDocumentEdit(JSON.stringify(updatedDoc, undefined, 2));
          setTimeout(() => setLowQualityStatus('lowQualityAction'), 3000); // Reset after 3 seconds
        } else {
          setLowQualityStatus('failed')
          setTimeout(() => setLowQualityStatus('lowQualityAction'), 3000); // Reset after 3 seconds
        }  
      } catch (e) {
        console.log(e)
        setLowQualityStatus('failed')
        setTimeout(() => setLowQualityStatus('lowQualityAction'), 3000); // Reset after 3 seconds
      }
    }

    if (lowQualityStatus === "lowQualityProcessing") {
      markLowQuality();
    }
  }, [lowQualityStatus, document]);

  // Handle invalid images action
  useEffect(() => {
    const markInvalidImages = async () => {
      const settings = {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          _id: document._id,
          status: "invalidImages"
        }),
      };

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_SERVER_URL}/api/documents/`,
          settings
        );
  
        console.log(response)
        if (response.status === 200) {
          setInvalidImagesStatus('invalidImagesProcessed')
          const updatedDoc = await response.json();
          setDocument(updatedDoc);
          setDocumentEdit(JSON.stringify(updatedDoc, undefined, 2));
          setTimeout(() => setInvalidImagesStatus('invalidImagesAction'), 3000); // Reset after 3 seconds
        } else {
          setInvalidImagesStatus('failed')
          setTimeout(() => setInvalidImagesStatus('invalidImagesAction'), 3000); // Reset after 3 seconds
        }  
      } catch (e) {
        console.log(e)
        setInvalidImagesStatus('failed')
        setTimeout(() => setInvalidImagesStatus('invalidImagesAction'), 3000); // Reset after 3 seconds
      }
    }

    if (invalidImagesStatus === "invalidImagesProcessing") {
      markInvalidImages();
    }
  }, [invalidImagesStatus, document]);

  // Handle refund action
  useEffect(() => {
    const processRefund = async () => {
      const settings = {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          _id: document._id,
          status: "toRefund"
        }),
      };

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_SERVER_URL}/api/documents/`,
          settings
        );
  
        console.log(response)
        if (response.status === 200) {
          setRefundStatus('refundProcessed')
          const updatedDoc = await response.json();
          setDocument(updatedDoc);
          setDocumentEdit(JSON.stringify(updatedDoc, undefined, 2));
          setTimeout(() => setRefundStatus('refundAction'), 3000); // Reset after 3 seconds
        } else {
          setRefundStatus('failed')
          setTimeout(() => setRefundStatus('refundAction'), 3000); // Reset after 3 seconds
        }  
      } catch (e) {
        console.log(e)
        setRefundStatus('failed')
        setTimeout(() => setRefundStatus('refundAction'), 3000); // Reset after 3 seconds
      }
    }

    if (refundStatus === "refunding") {
      processRefund();
    }
  }, [refundStatus, document]);

  useEffect(() => {
    const sendSms = async () => {
      const settings = {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          to: user.email,
          message: message,
        }),
      };

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_SERVER_URL}/api/user/message`,
          settings
        );
  
        console.log(response)
        if (response.status === 200) {
          setSmsStatus('sent')
        } else {
          setSmsStatus('failed')
        }  
      } catch (e) {
        console.log(e)
        setSmsStatus('failed')
      }
    }

    if (smsStatus === "sending") {
      //send the sms and set status to 'sent' or 'failed'
      sendSms();
    }
  }, [smsStatus]);

  return (
    <div className='d-flex flex-column min-vh-100'>
      <Header renderBg={true} className="py-2" />
      <div className='d-flex justify-content-end mx-5'>
        <h2>{t('account')}</h2>
      </div>
      <div className='d-flex flex-column align-items-center py-2'>
        <h3 className='mb-5'>{t('doc_title')}</h3>
        {document && (
          <div className="mb-4 p-3 border rounded">
            <h5>Current Status: <span className={`badge ${
              document.status === 'ready' ? 'bg-success' :
              document.status === 'lowQuality' ? 'bg-warning' :
              document.status === 'invalidImages' ? 'bg-danger' :
              document.status === 'toRefund' ? 'bg-secondary' :
              'bg-primary'
            }`}>{t(document.status)}</span></h5>
          </div>
        )}
        <h3>{t('front_image')}</h3>
        <div className="d-flex flex-column align-items-center mb-3">
          <img alt="frontImage" width="100%" style={{maxWidth: "700px"}} src={document && document.nationalLicenseAttributes ? document.nationalLicenseAttributes.frontImageUrl : ""} />
          {isAdmin && (
            <div className="d-flex flex-wrap justify-content-center gap-2 mt-2">
              <Button
                variant='secondary'
                size='sm'
                onClick={() => frontImageWidget.open()}
                disabled={isUploadingFront}
              >
                {isUploadingFront ? t('uploading') : t('replace_front_image')}
              </Button>
              <Button
                variant='outline-secondary'
                size='sm'
                onClick={() => startClipboardPaste('front')}
                disabled={isUploadingFront}
              >
                {clipboardTarget === 'front' ? 'Paste now' : 'Paste front image'}
              </Button>
              {clipboardTarget === 'front' && (
                <Button
                  variant='outline-danger'
                  size='sm'
                  onClick={cancelClipboardPaste}
                >
                  Cancel paste
                </Button>
              )}
            </div>
          )}
        </div>
        <h3>{t('back_image')}</h3>
        <div className="d-flex flex-column align-items-center mb-3">
          <img alt="backImage" width="100%" style={{maxWidth: "700px"}} src={document && document.nationalLicenseAttributes ? document.nationalLicenseAttributes.backImageUrl : ""} />
          {isAdmin && (
            <div className="d-flex flex-wrap justify-content-center gap-2 mt-2">
              <Button
                variant='secondary'
                size='sm'
                onClick={() => backImageWidget.open()}
                disabled={isUploadingBack}
              >
                {isUploadingBack ? t('uploading') : t('replace_back_image')}
              </Button>
              <Button
                variant='outline-secondary'
                size='sm'
                onClick={() => startClipboardPaste('back')}
                disabled={isUploadingBack}
              >
                {clipboardTarget === 'back' ? 'Paste now' : 'Paste back image'}
              </Button>
              {clipboardTarget === 'back' && (
                <Button
                  variant='outline-danger'
                  size='sm'
                  onClick={cancelClipboardPaste}
                >
                  Cancel paste
                </Button>
              )}
            </div>
          )}
        </div>
        <h3>{t('profile_image')}</h3>
        <img alt="profileImage" src={profileImage} />
        <h3>{t('document_section')}</h3>
        <Button variant='info' className='btn-white' size='lg' onClick={async () => {
          try {
            const response = await fetch(
              `${process.env.REACT_APP_API_SERVER_URL}/api/documents/pdf/${document ? document._id : ""}`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            );
            
            if (response.ok) {
              const blob = await response.blob();
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `document-${document._id}.pdf`;
              document.body.appendChild(a);
              a.click();
              window.URL.revokeObjectURL(url);
              document.body.removeChild(a);
            } else {
              alert('Failed to download PDF');
            }
          } catch (error) {
            console.error('Error downloading PDF:', error);
            alert('Error downloading PDF');
          }
        }}>{t('preview')}</Button>
        <textarea id="document" value={documentEdit} onChange={(e) => setDocumentEdit(e.target.value)}></textarea>
        <div className="d-flex flex-wrap gap-2 mb-3">
          <Button variant='info' className='btn-white' size='lg' onClick={(e) => {
            setDocStatus("saving");
          }} disabled={docStatus === "saving"}>{t(docStatus)}</Button>
          
          <Button variant='success' size='lg' onClick={(e) => {
            setApproveStatus("approving");
          }} disabled={approveStatus === "approving"}>{t(approveStatus)}</Button>
          
          <Button variant='warning' size='lg' onClick={(e) => {
            setLowQualityStatus("lowQualityProcessing");
          }} disabled={lowQualityStatus === "lowQualityProcessing"}>{t(lowQualityStatus)}</Button>
          
          <Button variant='danger' size='lg' onClick={(e) => {
            setInvalidImagesStatus("invalidImagesProcessing");
          }} disabled={invalidImagesStatus === "invalidImagesProcessing"}>{t(invalidImagesStatus)}</Button>
          
          <Button variant='secondary' size='lg' onClick={(e) => {
            if (window.confirm(t('refundConfirm') || 'Are you sure you want to process a refund? This action will trigger a refund email to the customer.')) {
              setRefundStatus("refunding");
            }
          }} disabled={refundStatus === "refunding"}>{t(refundStatus)}</Button>
        </div>
        <h3>user</h3>
        <textarea id="user" value={userEdit} onChange={(e) => setUserEdit(e.target.value)}></textarea>
        <Button variant='info' className='btn-white' size='lg' onClick={(e) => {
          setUserStatus("saving");
        }}>{t(userStatus)}</Button>
        <h3>send notification</h3>
        <textarea id="sms" value={message} onChange={(e) => setMessage(e.target.value)}></textarea>
        <Button variant='info' className='btn-white' size='lg' onClick={(e) => {
          setSmsStatus("sending");
        }}>{t(smsStatus)}</Button>
      </div>
      <Footer className='mt-auto' />
    </div>
  );
}
